import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  Button,
  Group,
  Stack,
  Avatar,
  FileInput,
  Divider,
  Text,
} from '@mantine/core';
import { IconUpload, IconUser, IconMail } from '@tabler/icons-react';
import { User } from '../../types';
import { createEnterKeyHandler } from '../../utils/enterKeyHandler';
import { useTranslation } from 'react-i18next';

interface ProfileEditModalProps {
  opened: boolean;
  onClose: () => void;
  userData: User;
  /** Отклоняется при ошибке — тогда окно не закрывается. */
  onSave: (formData: FormData) => Promise<unknown>;
}

export function ProfileEditModal({ opened, onClose, userData, onSave }: ProfileEditModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: userData.name,
    email: userData.email,
    position: userData.position || '',
    avatar: userData.avatar,
    bio: userData.bio,
  });

  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [isUploading, setUploading] = useState(false);

  useEffect(() => {
    if (userData) {
      setFormData({
        name: userData.name,
        email: userData.email,
        position: userData.position || '',
        avatar: userData.avatar,
        bio: userData.bio,
      });
      setAvatarFile(null);
    } else {
      setFormData({
        name: '',
        email: '',
        position: '',
        avatar: '',
        bio: '',
      });
      setAvatarFile(null);
    }
  }, [userData, opened]);

  const handleSave = async () => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('position', formData.position);
      fd.append('bio', formData.bio || '');
      if (avatarFile) fd.append('avatar', avatarFile);
      await onSave(fd);
      onClose();
    } catch {
      // Ошибку уже показал onSave; окно остаётся открытым, введённое не теряется.
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: userData.name,
      email: userData.email,
      position: userData.position || '',
      avatar: userData.avatar,
      bio: userData.bio || '',
    });
    setAvatarFile(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title={t('users.editProfile')}
      size="lg"
      centered
      onKeyDown={createEnterKeyHandler({ onSubmit: handleSave })}
    >
      <Stack gap="lg">
        {/* Аватар */}
        <Group gap="lg" align="center">
          <Avatar
            src={avatarFile ? URL.createObjectURL(avatarFile) : formData.avatar}
            size={80}
            radius="xl"
          />
          <Stack gap="xs" style={{ flex: 1 }}>
            <Text size="sm" fw={500}>
              {t('users.userPhoto')}
            </Text>
            <FileInput
              placeholder={t('users.selectFile')}
              accept="image/*"
              leftSection={<IconUpload size={16} />}
              value={avatarFile}
              onChange={setAvatarFile}
              size="sm"
            />
          </Stack>
        </Group>

        <Divider />

        {/* Основная информация */}
        <Stack gap="md">
          <Text size="sm" fw={500} c="dimmed">
            {t('users.basicInfo')}
          </Text>

          <TextInput
            label={t('users.name')}
            placeholder={t('users.enterName')}
            leftSection={<IconUser size={16} />}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />

          <TextInput
            label={t('users.email')}
            placeholder={t('users.enterEmail')}
            leftSection={<IconMail size={16} />}
            value={formData.email}
            description={t('profile.emailByAdmin')}
            variant="filled"
            readOnly
          />

          <TextInput
            label={t('users.position')}
            placeholder={t('users.enterPosition')}
            leftSection={<IconUser size={16} />}
            value={formData.position}
            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
          />

          <Textarea
            label={t('users.bio') || 'О себе'}
            placeholder={t('users.bio') || 'Расскажите немного о себе...'}
            value={formData.bio}
            onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
            minRows={3}
            rows={5}
          />
        </Stack>

        <Divider />

        {/* Кнопки */}
        <Group justify="flex-end" gap="sm">
          <Button variant="light" onClick={handleCancel} color="gray">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} color="teal" loading={isUploading}>
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
